import type {
  TaskSummaryDto,
  TaskPriority,
  GanttDependencyDto,
  GanttModel,
  GanttRow,
  GanttLink,
  GanttLinkType,
  GanttGroupBy,
} from '../types';
import { GANTT_LINK_TYPE } from '../types';
import type { PlannedDate } from '@/shared/types/datetime';
import { plannedToAnchorDate } from '@/shared/lib/datetime/planned-date';
import { priorityFromInt } from './priority.mapper';

export type PriorityKey = TaskPriority | 'none';

/** Bar/header color when grouping or coloring by priority. Reused by the Timeline's grouped layout. */
export const PRIORITY_COLOR: Record<PriorityKey, string> = {
  urgent: '#ef4444',
  high: '#f97316',
  medium: '#f59e0b',
  low: '#3b82f6',
  none: '#94a3b8',
};

/**
 * User-visible group-header labels the adapter cannot derive from data
 * (priority names, "unassigned", "no group"). Passed in by the hook so the
 * adapter stays pure and i18n-correct.
 */
export interface GanttGroupLabels {
  unassigned: string;
  noGroup: string;
  priority: Record<PriorityKey, string>;
}

/** A `PlannedDate` as a local noon-anchored `Date` for the chart (no off-by-one). */
function toDate(value: PlannedDate | null | undefined): Date | null {
  return value ? plannedToAnchorDate(value) : null;
}

function normalizeLinkType(type: string | null): GanttLinkType {
  switch ((type ?? '').toUpperCase()) {
    case 'SS':
      return GANTT_LINK_TYPE.StartToStart;
    case 'FF':
      return GANTT_LINK_TYPE.FinishToFinish;
    case 'SF':
      return GANTT_LINK_TYPE.StartToFinish;
    // Backend creates FS only; anything unrecognized defaults to FS.
    case 'FS':
    default:
      return GANTT_LINK_TYPE.FinishToStart;
  }
}

function priorityKey(value: number | string | null | undefined): PriorityKey {
  if (value == null || value === 'None') return 'none';
  const n = typeof value === 'number' ? value : parseInt(value, 10);
  if (isNaN(n)) return 'none';
  return priorityFromInt(n) ?? 'none';
}

function taskToRow(dto: TaskSummaryDto, parentId: string | null): GanttRow {
  return {
    id: dto.id,
    title: dto.title,
    startDate: toDate(dto.startDate),
    endDate: toDate(dto.dueDate),
    progress: dto.isCompleted ? 100 : 0,
    parentId,
    isGroupHeader: false,
    isCompleted: dto.isCompleted,
    color: dto.status?.color ?? PRIORITY_COLOR.none,
    assignee: dto.assignee
      ? {
          id: dto.assignee.id,
          name: dto.assignee.displayName ?? '—',
          avatarUrl: dto.assignee.avatarUrl ?? undefined,
        }
      : null,
    taskId: dto.id,
  };
}

/** Derives the group key/label/color for one task under a given grouping axis. Reused by the Timeline's grouped layout. */
export function groupOf(
  dto: TaskSummaryDto,
  groupBy: Exclude<GanttGroupBy, 'none'>,
  labels: GanttGroupLabels,
): { key: string; label: string; color?: string } {
  switch (groupBy) {
    case 'status':
      return {
        key: dto.status?.id ?? '__none',
        label: dto.status?.name ?? labels.noGroup,
        color: dto.status?.color ?? undefined,
      };
    case 'priority': {
      const k = priorityKey(dto.priority);
      return { key: k, label: labels.priority[k], color: PRIORITY_COLOR[k] };
    }
    case 'assignee':
      return dto.assignee
        ? { key: dto.assignee.id, label: dto.assignee.displayName ?? labels.unassigned }
        : { key: '__unassigned', label: labels.unassigned };
    case 'group':
      return dto.group
        ? { key: dto.group.id, label: dto.group.name ?? labels.noGroup }
        : { key: '__nogroup', label: labels.noGroup };
  }
}

function aggregateDates(children: GanttRow[]): { start: Date | null; end: Date | null } {
  const starts = children.map((c) => c.startDate).filter((d): d is Date => d != null);
  const ends = children.map((c) => c.endDate).filter((d): d is Date => d != null);
  return {
    start: starts.length ? new Date(Math.min(...starts.map((d) => d.getTime()))) : null,
    end: ends.length ? new Date(Math.max(...ends.map((d) => d.getTime()))) : null,
  };
}

/**
 * Builds the normalized {@link GanttModel} the chart consumes.
 *
 * - `groupBy === 'none'`: rows keep real parent→subtask hierarchy via `parent.id`.
 * - otherwise: one synthetic header row per group (`isGroupHeader`), with every
 *   task flattened beneath it (real subtask nesting is dropped in v1).
 *
 * Links are normalized and filtered to edges whose endpoints both exist in the
 * row set, so no arrow dangles to a task that was filtered out.
 */
export function tasksToGanttModel(
  tasks: TaskSummaryDto[],
  deps: GanttDependencyDto[],
  groupBy: GanttGroupBy,
  labels: GanttGroupLabels,
): GanttModel {
  const rows: GanttRow[] = [];

  if (groupBy === 'none') {
    for (const t of tasks) rows.push(taskToRow(t, t.parent?.id ?? null));
  } else {
    const order: string[] = [];
    const headers = new Map<string, { label: string; color?: string; children: GanttRow[] }>();

    for (const t of tasks) {
      const g = groupOf(t, groupBy, labels);
      const headerId = `group:${g.key}`;
      let bucket = headers.get(headerId);
      if (!bucket) {
        bucket = { label: g.label, color: g.color, children: [] };
        headers.set(headerId, bucket);
        order.push(headerId);
      }
      bucket.children.push(taskToRow(t, headerId));
    }

    for (const headerId of order) {
      const { label, color, children } = headers.get(headerId)!;
      const { start, end } = aggregateDates(children);
      const progress = children.length
        ? Math.round(children.reduce((sum, c) => sum + c.progress, 0) / children.length)
        : 0;
      rows.push({
        id: headerId,
        title: label,
        startDate: start,
        endDate: end,
        progress,
        parentId: null,
        isGroupHeader: true,
        isCompleted: false,
        color,
      });
      rows.push(...children);
    }
  }

  const taskIds = new Set(tasks.map((t) => t.id));
  const links: GanttLink[] = deps
    .filter((d) => taskIds.has(d.predecessorTaskId) && taskIds.has(d.successorTaskId))
    .map((d) => ({
      id: d.id,
      from: d.predecessorTaskId,
      to: d.successorTaskId,
      type: normalizeLinkType(d.type),
      lagDays: d.lagDays ?? 0,
    }));

  return { rows, links };
}
